import { observer } from "mobx-react-lite";

import * as Node from "@/components/expression/utils/layout";
import { EditNodeInput } from "@/components/input/EditNodeInput";
import { getNumberDisplayValue } from "@/utils/getNumberDisplayValue";
import { twMerge } from "@/utils/twMerge";

import { UnitNode } from "./UnitNode";
import { NodeAttachments } from "./utils/NodeAttachments";

import type { ExpressionComponent } from "./types";

export const NumberNode: ExpressionComponent<"number"> = observer(function NumberNode({
  node,
  className,
}) {
  const value = getNumberDisplayValue(node.value);
  const isEditing = node.doc.input.isEditing(node);

  let label: string | undefined = parseFloat(node.value).toString();
  if (label === value) {
    label = undefined;
  }

  return (
    <Node.Container
      node={node}
      dimension="tight"
      data-testid="atomic-node"
      className={twMerge(
        "before:content-zero-space",
        isEditing && "px-0",
        node.unitNode && isEditing && "mr-0.5",
      )}
      disableHover
    >
      {node.unitNode?.placement === "prefix" && <UnitNode node={node.unitNode} />}
      <EditNodeInput
        node={node}
        className={twMerge(node.unitNode && "-mr-0.5")}
        inputClassName={twMerge("rounded-full", className)}
      >
        <Node.Value
          node={node}
          aria-label={label}
          isHighlighted={node.isHighlighted}
          highlightClassName="-bottom-[1.5px] -mx-[2px]"
        >
          {value}
        </Node.Value>
      </EditNodeInput>
      {(node.power || node.subscript) && (
        <NodeAttachments
          position="right"
          styleProviderValue={{ size: "xs" }}
          attachments={[
            {
              node: node.power?.parameters[0],
              position: "right-top",
            },
            {
              node: node.subscript?.parameters[0],
              position: "right-bottom",
            },
          ]}
        />
      )}
      {node.unitNode?.placement === "postfix" && <UnitNode node={node.unitNode} />}
    </Node.Container>
  );
});
